import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, ExternalLink, Search, RefreshCw, Globe, User, Archive, Inbox, ChevronLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import supabase from '../lib/supabaseClient';
import MessengerIcon from '../components/MessengerIcon';
import { wasNeverSent } from '../lib/messageVisibility';

// ─── Channel config ────────────────────────────────────────────────────────────
// ONE inbox, two channels. `web` is the original behaviour, unchanged: a conversation is a
// leads row with a session_id and its messages are keyed by session_id. `messenger` has no
// session_id (leads.channel='messenger', identified by external_id), so a conversation is
// keyed by the lead id and its messages by messages.lead_id. `key(row)` reads that key off a
// lead; `msgKey(row)` reads it off a message.
const CHANNEL_CONFIG = {
  web: {
    title: 'Web Chat',
    Icon: Globe,
    msgChannel: 'web',
    keyCol: 'session_id',
    key: (r) => r.session_id,
    msgKey: (m) => m.session_id,
    isOurLead: (l) => !!l.session_id,
    anonymous: 'Anonymous Visitor',
    emptyActive: 'Visitors who send a message on your site will appear here in real time.',
    leadCols: 'id, name, email, status, created_at, session_id',
    msgCols: 'id, session_id, direction, message_body, timestamp, metadata',
    previewCols: 'session_id, message_body, direction, timestamp',
  },
  messenger: {
    title: 'Messenger',
    Icon: MessengerIcon,
    msgChannel: 'messenger',
    keyCol: 'lead_id',
    key: (r) => r.id,
    msgKey: (m) => m.lead_id,
    isOurLead: (l) => l.channel === 'messenger',
    anonymous: 'Messenger contact',
    emptyActive: 'People who message your connected Facebook Page will appear here in real time.',
    leadCols: 'id, name, email, status, created_at, session_id, channel',
    // status / error_code: a Messenger reply that Meta refused (e.g. the 24h window closed)
    // leaves a failed row; it is not part of the conversation the person saw.
    msgCols: 'id, lead_id, direction, message_body, timestamp, metadata, status, error_code',
    previewCols: 'lead_id, message_body, direction, timestamp, status, error_code',
  },
};
// Normalise a lead row into a list item whose `_key` is this channel's conversation key.
const toSession = (cfg, lead, extra = {}) => ({ ...lead, _key: cfg.key(lead), ...extra });
// Messenger only. Web rows are never hidden (the web inbox never filtered).
const hiddenMessengerMsg = (m) =>
  m.direction === 'outbound' && (m.status === 'failed' || m.status === 'cancelled' || wasNeverSent(m));

// ─── Constants ─────────────────────────────────────────────────────────────────
const ARCHIVE_AFTER_MS = 24 * 60 * 60 * 1000; // 24 hours of inactivity → archived
const SYSTEM_MESSAGES = ['__page_open__'];
const isSystemMsg = (body) => SYSTEM_MESSAGES.includes(body);

// ─── Timezone helpers ───────────────────────────────────────────────────────────
const TZ_MAP = {
  'EST': 'America/New_York',    'EDT': 'America/New_York',
  'CST': 'America/Chicago',     'CDT': 'America/Chicago',
  'MST': 'America/Denver',      'MDT': 'America/Denver',
  'PST': 'America/Los_Angeles', 'PDT': 'America/Los_Angeles',
  'AKST': 'America/Anchorage',  'HST': 'Pacific/Honolulu',
};
const resolveTimezone = (raw) => {
  if (!raw) return undefined;
  return TZ_MAP[raw.trim().toUpperCase()] || raw;
};

function relativeTime(ts, timezone) {
  if (!ts) return '';
  const raw = typeof ts === 'string' && !ts.includes('Z') ? ts + 'Z' : ts;
  const d = new Date(raw);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  const tz = resolveTimezone(timezone);
  const opts = { timeZone: tz };
  const todayStr = now.toLocaleDateString('en-US', opts);
  const dStr = d.toLocaleDateString('en-US', opts);
  if (diff < 86400 && todayStr === dStr)
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...opts });
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dStr === yesterday.toLocaleDateString('en-US', opts))
    return `Yesterday ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...opts })}`;
  if (diff < 604800)
    return d.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit', ...opts });
  return `${Math.floor(diff / 86400)}d ago`;
}

function fullTime(ts, timezone) {
  if (!ts) return '';
  const raw = typeof ts === 'string' && !ts.includes('Z') ? ts + 'Z' : ts;
  const tz = resolveTimezone(timezone);
  return new Date(raw).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    ...(tz ? { timeZone: tz } : {}),
  });
}

// ─── Status badge ───────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  'New Lead':     'bg-blue-50 text-blue-600 ring-1 ring-blue-200',
  'Cold Lead':    'bg-slate-50 text-slate-500 ring-1 ring-slate-200',
  'Warm Lead':    'bg-amber-50 text-amber-600 ring-1 ring-amber-200',
  'Engaging':     'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200',
  'Hot Lead':     'bg-red-50 text-red-600 ring-1 ring-red-200',
  'Won':          'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200',
  'Lost':         'bg-rose-50 text-rose-500 ring-1 ring-rose-200',
  'dead':         'bg-slate-50 text-slate-400 ring-1 ring-slate-200',
  'Disqualified': 'bg-red-50 text-red-400 ring-1 ring-red-200',
};
function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || 'bg-slate-50 text-slate-500 ring-1 ring-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status || 'Unknown'}
    </span>
  );
}

// ─── Avatar ─────────────────────────────────────────────────────────────────────
function Avatar({ name, unread }) {
  const initials = name
    ? name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : null;
  return (
    <div className="relative flex-shrink-0">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold select-none
                       ${initials ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
        {initials || <User className="w-4 h-4" />}
      </div>
      {unread && (
        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-indigo-500 rounded-full border-2 border-white" />
      )}
    </div>
  );
}

// ─── Session row ────────────────────────────────────────────────────────────────
function SessionRow({ session, selected, unread, onClick, timezone, anonymous = 'Anonymous Visitor' }) {
  const displayName = session.name || session.email || anonymous;
  const rawPreview = session._lastMsg?.message_body;
  const preview = (!rawPreview || isSystemMsg(rawPreview)) ? 'Started a conversation' : rawPreview;
  const ts = session._lastMsg?.timestamp || session.created_at;
  const visitorWaiting = session._lastMsg?.direction === 'inbound' && !isSystemMsg(session._lastMsg?.message_body);

  return (
    <button
      onClick={() => onClick(session)}
      className={`w-full text-left px-4 py-3.5 border-b border-slate-100 transition-all duration-150
                  border-l-[3px]
                  ${selected
                    ? 'bg-indigo-50/80 border-l-indigo-500'
                    : 'hover:bg-slate-50/80 border-l-transparent'}`}
    >
      <div className="flex items-start gap-3">
        <Avatar name={session.name} unread={unread} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-0.5">
            <span className={`text-sm truncate ${unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-800'}`}>
              {displayName}
            </span>
            <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0 tabular-nums">
              {relativeTime(ts, timezone)}
            </span>
          </div>
          <p className={`text-xs truncate leading-relaxed ${unread ? 'text-slate-600 font-medium' : 'text-slate-400'}`}>
            {!visitorWaiting && rawPreview && !isSystemMsg(rawPreview) && (
              <span className="text-slate-300 mr-1">AI:</span>
            )}
            {preview}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <StatusBadge status={session.status} />
            {visitorWaiting && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full font-medium ring-1 ring-amber-200">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                Waiting
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Message bubble ─────────────────────────────────────────────────────────────
function MessageBubble({ msg, timezone, Icon = Globe }) {
  if (isSystemMsg(msg.message_body)) return null;
  const isInbound = msg.direction === 'inbound';
  return (
    <div className={`flex ${isInbound ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isInbound && (
        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
          <Icon className="w-3 h-3 text-indigo-600" />
        </div>
      )}
      <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm shadow-sm
                       ${isInbound
                         ? 'bg-indigo-600 text-white rounded-br-sm'
                         : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
        <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.message_body}</p>
        <p className={`text-xs mt-1.5 ${isInbound ? 'text-indigo-200' : 'text-slate-400'}`}>
          {fullTime(msg.timestamp, timezone)}
        </p>
      </div>
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────────
function EmptyState({ tab, search, activeText = 'Visitors who send a message on your site will appear here in real time.' }) {
  if (search) return (
    <div className="flex flex-col items-center justify-center h-48 text-center px-6">
      <Search className="w-8 h-8 text-slate-200 mb-3" />
      <p className="text-sm font-medium text-slate-500">No results for "{search}"</p>
      <p className="text-xs text-slate-400 mt-1">Try a different name or email</p>
    </div>
  );
  if (tab === 'active') return (
    <div className="flex flex-col items-center justify-center h-56 text-center px-8">
      <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
        <Inbox className="w-5 h-5 text-indigo-400" />
      </div>
      <p className="text-sm font-semibold text-slate-600">No active conversations</p>
      <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
        {activeText}
      </p>
    </div>
  );
  return (
    <div className="flex flex-col items-center justify-center h-56 text-center px-8">
      <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
        <Archive className="w-5 h-5 text-slate-400" />
      </div>
      <p className="text-sm font-semibold text-slate-600">No archived conversations</p>
      <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
        Conversations older than 24 hours are automatically moved here.
      </p>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────────
export default function WebChatInbox({ channel = 'web' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cfg = CHANNEL_CONFIG[channel] || CHANNEL_CONFIG.web;
  const isMessenger = channel === 'messenger';
  // Keeps the realtime channel names of the Web Chat page exactly as they were.
  const sfx = isMessenger ? '-messenger' : '';
  const ChannelIcon = cfg.Icon;

  const [sessions, setSessions]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('active');
  const [search, setSearch]           = useState('');
  const [selected, setSelected]       = useState(null);
  const [showMobileList, setShowMobileList] = useState(true);
  const [messages, setMessages]       = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [unreadIds, setUnreadIds]     = useState(new Set());
  const [timezone, setTimezone]       = useState(null);

  const selectedRef       = useRef(null);
  const messagesEndRef    = useRef(null);
  const realtimeRef       = useRef(null);
  const leadsChannelRef   = useRef(null);
  const msgsListChannelRef = useRef(null);

  // ── Timezone ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.tenant_id) return;
    supabase
      .from('platform_settings')
      .select('value')
      .eq('tenant_id', user.tenant_id)
      .eq('key', 'timezone')
      .maybeSingle()
      .then(({ data }) => { if (data?.value) setTimezone(data.value); });
  }, [user?.tenant_id]);

  // ── Build enriched session list from leads + messages ───────────────────────
  const buildSessions = useCallback(async (silent = false) => {
    if (!user?.tenant_id) return;
    if (!silent) setLoading(true);
    try {
      let leadsQuery = supabase
        .from('leads')
        .select(cfg.leadCols)
        .eq('tenant_id', user.tenant_id);
      leadsQuery = cfg.keyCol === 'session_id'
        ? leadsQuery.not('session_id', 'is', null)
        : leadsQuery.eq('channel', cfg.msgChannel);
      const { data: leads, error } = await leadsQuery
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      if (!leads?.length) { setSessions([]); return; }

      const sessionIds = leads.map(l => cfg.key(l));

      // Latest message per session (for preview + timestamp)
      const { data: msgs } = await supabase
        .from('messages')
        .select(cfg.previewCols)
        .in(cfg.keyCol, sessionIds)
        .order('timestamp', { ascending: false })
        .limit(500);

      // Sessions with at least 1 real inbound message (visitor actually typed something)
      const { data: inbound } = await supabase
        .from('messages')
        .select(cfg.keyCol)
        .in(cfg.keyCol, sessionIds)
        .eq('direction', 'inbound')
        .not('message_body', 'in', '("__page_open__")')
        .limit(500);

      const hasInboundSet = new Set((inbound || []).map(m => cfg.msgKey(m)));
      const latestBySession = {};
      (msgs || []).forEach(m => {
        if (isMessenger && hiddenMessengerMsg(m)) return;
        if (!latestBySession[cfg.msgKey(m)]) latestBySession[cfg.msgKey(m)] = m;
      });

      const enriched = leads.map(l => toSession(cfg, l, {
        _lastMsg:    latestBySession[cfg.key(l)] || null,
        _hasInbound: hasInboundSet.has(cfg.key(l)),
      }));

      enriched.sort((a, b) => {
        const ta = a._lastMsg?.timestamp || a.created_at;
        const tb = b._lastMsg?.timestamp || b.created_at;
        return new Date(tb) - new Date(ta);
      });

      setSessions(enriched);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user?.tenant_id, cfg, isMessenger]);

  useEffect(() => { buildSessions(); }, [buildSessions]);

  // ── Silent polling fallback (15s) ───────────────────────────────────────────
  useEffect(() => {
    if (!user?.tenant_id) return;
    const interval = setInterval(() => buildSessions(true), 15000);
    return () => clearInterval(interval);
  }, [user?.tenant_id, buildSessions]);

  // ── Realtime: leads INSERT / UPDATE ─────────────────────────────────────────
  useEffect(() => {
    if (!user?.tenant_id) return;
    const channel = supabase
      .channel(`wchat-new-leads${sfx}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'leads',
          filter: `tenant_id=eq.${user.tenant_id}` }, (payload) => {
        const lead = payload.new;
        if (!cfg.isOurLead(lead)) return;
        setSessions(prev => {
          if (prev.some(s => s.id === lead.id)) return prev;
          return [toSession(cfg, lead, { _lastMsg: null, _hasInbound: false }), ...prev];
        });
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'leads',
          filter: `tenant_id=eq.${user.tenant_id}` }, (payload) => {
        const lead = payload.new;
        if (!cfg.isOurLead(lead)) return;
        setSessions(prev =>
          prev.map(s => s.id === lead.id
            ? { ...s, name: lead.name, email: lead.email, status: lead.status }
            : s)
        );
        setSelected(prev => prev?.id === lead.id
          ? { ...prev, name: lead.name, email: lead.email, status: lead.status }
          : prev
        );
      })
      .subscribe();
    leadsChannelRef.current = channel;
    return () => { supabase.removeChannel(channel); };
  }, [user?.tenant_id, cfg, sfx]);

  // ── Realtime: new messages → update previews, mark unread ──────────────────
  useEffect(() => {
    if (!user?.tenant_id) return;
    const channel = supabase
      .channel(`wchat-all-messages${sfx}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages',
          filter: `tenant_id=eq.${user.tenant_id}` }, (payload) => {
        const msg = payload.new;
        const msgKey = cfg.msgKey(msg);
        if (msg.channel !== cfg.msgChannel || !msgKey) return;
        if (isSystemMsg(msg.message_body)) return;
        // Messenger: a reply Meta refused is not part of the conversation.
        if (isMessenger && hiddenMessengerMsg(msg)) return;

        const isRealInbound = msg.direction === 'inbound';

        setSessions(prev => {
          const idx = prev.findIndex(s => s._key === msgKey);
          if (idx === -1) {
            // New session not yet in list — fetch and prepend
            supabase
              .from('leads')
              .select(cfg.leadCols)
              .eq(cfg.keyCol === 'session_id' ? 'session_id' : 'id', msgKey)
              .eq('tenant_id', user.tenant_id)
              .maybeSingle()
              .then(({ data: lead }) => {
                if (!lead) return;
                setSessions(p => {
                  if (p.some(s => s._key === cfg.key(lead))) return p;
                  return [toSession(cfg, lead, { _lastMsg: msg, _hasInbound: isRealInbound }), ...p];
                });
              });
            return prev;
          }
          const updated = {
            ...prev[idx],
            _lastMsg: msg,
            _hasInbound: prev[idx]._hasInbound || isRealInbound,
          };
          const rest = prev.filter((_, i) => i !== idx);
          return [updated, ...rest];
        });

        if (selectedRef.current?._key !== msgKey) {
          setUnreadIds(prev => { const next = new Set(prev); next.add(msgKey); return next; });
        }
      })
      .subscribe();
    msgsListChannelRef.current = channel;
    return () => { supabase.removeChannel(channel); };
  }, [user?.tenant_id, cfg, sfx, isMessenger]);

  // ── Fetch messages for selected session ─────────────────────────────────────
  const fetchMessages = useCallback(async (sessionId) => {
    setMsgsLoading(true);
    setMessages([]);
    const { data, error } = await supabase
      .from('messages')
      .select(cfg.msgCols)
      .eq(cfg.keyCol, sessionId)
      .order('timestamp', { ascending: true });
    if (!error && data) setMessages(isMessenger ? data.filter(m => !hiddenMessengerMsg(m)) : data);
    setMsgsLoading(false);
  }, [cfg, isMessenger]);

  const handleSelectSession = (session) => {
    setSelected(session);
    selectedRef.current = session;
    fetchMessages(session._key);
    setUnreadIds(prev => { const next = new Set(prev); next.delete(session._key); return next; });
    setShowMobileList(false);
  };

  // ── Auto-scroll ──────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Realtime: open conversation ──────────────────────────────────────────────
  useEffect(() => {
    if (realtimeRef.current) { supabase.removeChannel(realtimeRef.current); realtimeRef.current = null; }
    if (!selected?._key) return;
    const channel = supabase
      .channel(`wchat-inbox${sfx}-${selected._key}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages',
          filter: `${cfg.keyCol}=eq.${selected._key}` }, (payload) => {
        if (isMessenger && hiddenMessengerMsg(payload.new)) return;
        if (!isSystemMsg(payload.new.message_body)) {
          setMessages(prev => [...prev, payload.new]);
        }
      });
    // Messenger: a send is written 'pending' and then flips to 'sent' or 'failed'; drop it
    // from the open thread if it ends up refused. (Web rows never change status.)
    if (isMessenger) {
      channel.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages',
          filter: `${cfg.keyCol}=eq.${selected._key}` }, (payload) => {
        setMessages(prev => hiddenMessengerMsg(payload.new)
          ? prev.filter(m => m.id !== payload.new.id)
          : prev.map(m => (m.id === payload.new.id ? { ...m, ...payload.new } : m)));
      });
    }
    channel.subscribe();
    realtimeRef.current = channel;
    return () => { supabase.removeChannel(channel); realtimeRef.current = null; };
  }, [selected?._key, cfg, sfx, isMessenger]);

  // ── Classify sessions ────────────────────────────────────────────────────────
  const now = Date.now();
  const engaged = sessions.filter(s => s._hasInbound);

  const activeSessions = engaged.filter(s => {
    const ts = s._lastMsg?.timestamp || s.created_at;
    const raw = typeof ts === 'string' && !ts.includes('Z') ? ts + 'Z' : ts;
    return now - new Date(raw).getTime() < ARCHIVE_AFTER_MS;
  });

  const archivedSessions = engaged.filter(s => {
    const ts = s._lastMsg?.timestamp || s.created_at;
    const raw = typeof ts === 'string' && !ts.includes('Z') ? ts + 'Z' : ts;
    return now - new Date(raw).getTime() >= ARCHIVE_AFTER_MS;
  });

  const currentList = activeTab === 'active' ? activeSessions : archivedSessions;

  const filtered = currentList.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
  });

  const activeUnread = unreadIds.size > 0
    ? [...unreadIds].filter(sid => activeSessions.some(s => s._key === sid)).length
    : 0;

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-64px)] bg-slate-50 overflow-hidden relative">

      {/* ── Left panel ── */}
      <div className={`${showMobileList ? 'flex' : 'hidden'} md:flex w-full md:w-[300px] flex-shrink-0 bg-white border-r border-slate-200 flex-col absolute inset-0 md:relative z-10`}>

        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <ChannelIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 className="font-semibold text-slate-900 text-[15px]">{cfg.title}</h2>
            </div>
            <button
              onClick={() => buildSessions(true)}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all
                          ${activeTab === 'active'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Inbox className="w-3 h-3" />
              Active
              {activeSessions.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                                  ${activeTab === 'active'
                                    ? activeUnread > 0 ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-600'
                                    : 'bg-slate-200 text-slate-500'}`}>
                  {activeUnread > 0 ? activeUnread : activeSessions.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-all
                          ${activeTab === 'archived'
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Archive className="w-3 h-3" />
              Archived
              {archivedSessions.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                                  ${activeTab === 'archived' ? 'bg-slate-100 text-slate-600' : 'bg-slate-200 text-slate-500'}`}>
                  {archivedSessions.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg
                         placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-300 transition-all"
            />
          </div>
        </div>

        {/* Session list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-slate-400">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState tab={activeTab} search={search} activeText={cfg.emptyActive} />
          ) : (
            filtered.map(s => (
              <SessionRow
                key={s.id}
                session={s}
                selected={selected?.id === s.id}
                unread={unreadIds.has(s._key)}
                onClick={handleSelectSession}
                timezone={timezone}
                anonymous={cfg.anonymous}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className={`flex-1 ${!showMobileList ? 'flex' : 'hidden'} md:flex flex-col min-w-0 bg-slate-50`}>
        {selected ? (
          <>
            {/* Thread header */}
            <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowMobileList(true)}
                  className="md:hidden p-1.5 -ml-1 rounded-lg hover:bg-slate-100 text-slate-500"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <Avatar name={selected.name} unread={false} />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 text-[15px]">
                      {selected.name || selected.email || cfg.anonymous}
                    </h3>
                    <StatusBadge status={selected.status} />
                  </div>
                  {selected.email && (
                    <p className="text-xs text-slate-400 mt-0.5">{selected.email}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => navigate(`/lead/${selected.id}`)}
                className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700
                           bg-indigo-50 hover:bg-indigo-100 px-3.5 py-2 rounded-lg transition-colors border border-indigo-100"
              >
                View Lead Profile
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {msgsLoading ? (
                <div className="flex items-center justify-center h-24 text-slate-400">
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm">Loading messages...</span>
                </div>
              ) : messages.filter(m => !isSystemMsg(m.message_body)).length === 0 ? (
                <div className="flex items-center justify-center h-24">
                  <p className="text-sm text-slate-400">No messages in this conversation</p>
                </div>
              ) : (
                <>
                  {messages.map(msg => (
                    <MessageBubble key={msg.id} msg={msg} timezone={timezone} Icon={ChannelIcon} />
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-4 shadow-sm">
              <MessageCircle className="w-7 h-7 text-indigo-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700">Select a conversation</p>
            <p className="text-xs text-slate-400 mt-1.5 max-w-[220px] leading-relaxed">
              Click any conversation on the left to view the full message thread.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
